import { useCallback } from 'react';
import type { UserEntity } from '@user/UserEntity';
import { FlatButton } from '@framework/ui/atoms';
import { generateDefaultUserIcon } from '@framework/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faExternalLinkAlt, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import { Trans, useTranslation } from 'react-i18next';
type ContactLinkProps = { children?: React.ReactNode };
const ContactLink: React.FC<ContactLinkProps> = ({ children }: ContactLinkProps) => (
  <a
    href="https://balus-help.levii.co.jp/hc/ja/requests/new"
    target="_blank"
    rel="noreferrer"
    className="text-blue-700 hover:underline"
  >
    {children} <FontAwesomeIcon icon={faExternalLinkAlt} />
  </a>
);

type Props = {
  userEntity: UserEntity;
  name: string;
  imageUrl: string | null;
  // Firebase Auth の providerData ベースの Google 連携状態
  googleLinked: boolean;
  // Firebase Auth の providerData ベースの Microsoft 連携状態
  microsoftLinked: boolean;
  onChangeName(name: string): void;
  onCreatePassword(): void;
  onChangePassword(): void;
  onChangeEmail(): void;
  onCancelPendingMailAddressChange(): Promise<void>;
  onConnectGoogle(): void;
  onDisconnectGoogle(): void;
  onConnectMicrosoft(): void;
  onDisconnectMicrosoft(): void;
  onImageClear(): void;
  onUploadImage(event: React.ChangeEvent<HTMLInputElement>): void;
};

export const EditUserForm: React.FC<Props> = ({
  userEntity,
  name,
  imageUrl,
  googleLinked,
  microsoftLinked,
  onChangeName,
  onCreatePassword,
  onChangePassword,
  onChangeEmail,
  onCancelPendingMailAddressChange,
  onConnectGoogle,
  onDisconnectGoogle,
  onConnectMicrosoft,
  onDisconnectMicrosoft,
  onImageClear,
  onUploadImage,
}: Props) => {
  const { t } = useTranslation();
  const handleChangeName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChangeName(event.target.value);
    },
    [onChangeName]
  );

  const handleCancelPendingMailAddressChange = useCallback(async () => {
    try {
      await onCancelPendingMailAddressChange();
      toast.success(t('auth.mailAddressChange.modal.cancelSuccess'));
    } catch (e) {
      toast.error(t('auth.mailAddressChange.modal.cancelError'));
      console.error('Failed to cancel pending mail address change:', e);
    }
  }, [onCancelPendingMailAddressChange, t]);

  const canChangeMailAddressBySelf = !googleLinked && !microsoftLinked;

  return (
    <>
      <div className="flex items-center justify-between">
        {imageUrl ? (
          <div className="relative size-16">
            <div className="absolute size-16 overflow-hidden rounded-full">
              <img className="size-full" src={imageUrl} alt={userEntity.name} />
            </div>
            <div className="absolute flex size-16 items-start justify-end">
              <div className="relative flex cursor-pointer items-center justify-center" onClick={onImageClear}>
                <FontAwesomeIcon icon={faCircle} className="absolute text-2xl text-red-500" />
                <FontAwesomeIcon icon={faTimes} className="absolute text-xl text-white" />
              </div>
            </div>
          </div>
        ) : (
          <div className="relative size-16 overflow-hidden rounded-full">
            <div className="absolute size-full opacity-50">
              {generateDefaultUserIcon({
                id: userEntity.id,
                name: userEntity.name.toString(),
                isAnonymous: false,
                size: 64,
              })}
            </div>
            <div className="absolute flex size-full items-center justify-center">
              <FontAwesomeIcon icon={faPlus} className="text-2xl text-blue-500" />
            </div>
            <input
              type="file"
              id="icon-image"
              className="absolute size-full cursor-pointer opacity-0"
              accept="image/png, image/jpeg"
              onChange={onUploadImage}
            />
          </div>
        )}
        <div className="flex w-5/6 flex-col">
          <div className="pb-2 font-bold">{t('auth.editUser.form.nameLabel')}</div>
          <input type="text" className="rounded-sm border p-2" value={name} onChange={handleChangeName} autoFocus />
        </div>
      </div>

      <div className="flex flex-row justify-between pb-2 pt-8 font-bold">
        {t('auth.editUser.form.emailLabel')}
        {canChangeMailAddressBySelf && (
          <FlatButton onClick={onChangeEmail}>{t('auth.editUser.form.mailAddressChangeButton')}</FlatButton>
        )}
      </div>
      <input
        type="text"
        className="cursor-not-allowed rounded-sm border bg-gray-200 p-2"
        defaultValue={userEntity.mailAddress}
        disabled
      />
      {canChangeMailAddressBySelf ? (
        userEntity.pendingMailAddress && (
          <div className="flex flex-row items-center justify-between pt-1 text-xs">
            <span>
              {t('auth.mailAddressChange.modal.pendingLabel', { mailAddress: userEntity.pendingMailAddress })}
            </span>
            <FlatButton onClick={handleCancelPendingMailAddressChange}>{t('common.action.cancel')}</FlatButton>
          </div>
        )
      ) : (
        <div className="pt-1 text-xs">
          <Trans i18nKey="auth.editUser.form.emailChangeNote" components={{ contactLink: <ContactLink /> }} />
        </div>
      )}

      <div className="flex flex-row justify-between pb-2 pt-8 font-bold">
        {t('auth.editUser.form.passwordLabel')}
        {userEntity.hasPassword ? (
          <FlatButton onClick={onChangePassword}>{t('auth.editUser.form.passwordChange')}</FlatButton>
        ) : (
          <FlatButton onClick={onCreatePassword}>{t('common.action.add')}</FlatButton>
        )}
      </div>

      <div className="pb-2 pt-8 font-bold">{t('auth.editUser.form.accountIntegrations')}</div>

      {/* Firebase Auth の providerData で連携状態を判定する */}
      <div className="flex flex-row justify-between pb-2">
        <span>Google</span>
        {googleLinked ? (
          <FlatButton onClick={onDisconnectGoogle}>{t('auth.editUser.form.disconnect')}</FlatButton>
        ) : (
          <FlatButton onClick={onConnectGoogle}>{t('auth.editUser.form.connect')}</FlatButton>
        )}
      </div>

      <div className="flex flex-row justify-between pb-2">
        <span>Microsoft</span>
        {microsoftLinked ? (
          <FlatButton onClick={onDisconnectMicrosoft}>{t('auth.editUser.form.disconnect')}</FlatButton>
        ) : (
          <FlatButton onClick={onConnectMicrosoft}>{t('auth.editUser.form.connect')}</FlatButton>
        )}
      </div>
    </>
  );
};
